import { useState } from 'react';
import { jobAPI } from '../../../services/api';

// Extracted verbatim from JobManagement.js (Task 4).
//
// `isLoading` stays owned by the orchestrator — this hook receives
// `setIsLoading` and flips it exactly as the original inline functions did.
//
// Coupling note (reported, not improvised around): the original
// `loadCustomerJobs` also set `selectedCustomer` and `showCustomerJobs`,
// both orchestrator-owned UI state (they gate a modal rendered outside this
// hook's concern and aren't part of this hook's documented return shape).
// Rather than duplicate that state here or reach into the orchestrator from
// inside the hook, the two setters are threaded in as constructor arguments
// — the same pattern already used for `setIsLoading`.
export const useCustomers = ({ setIsLoading, setSelectedCustomer, setShowCustomerJobs }) => {
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customersPage, setCustomersPage] = useState(1);
  const [customersTotalPages, setCustomersTotalPages] = useState(1);
  const [customerJobs, setCustomerJobs] = useState([]);
  // #52: overlay-scoped loading, deliberately NOT the orchestrator's shared
  // `isLoading`. That flag is passed to DataTable as `loading`, so flipping it
  // swaps the table out for a loading state and unmounts the row button that
  // opened the overlay. Modal.js then has no live node to restore focus to on
  // close, and a keyboard user is dropped back to <body>. Anything that runs
  // while an overlay is open must use this instead.
  const [overlayLoading, setOverlayLoading] = useState(false);

  const loadCustomers = async () => {
    try {
      setIsLoading(true);
      const data = await jobAPI.getCustomersList({
        page: customersPage,
        page_size: 20,
        search: customerSearch || undefined
      });
      setCustomers(data.customers || []);
      setCustomersTotalPages(data.total_pages || 1);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCustomerJobs = async (customerName) => {
    try {
      setOverlayLoading(true);
      const data = await jobAPI.getCustomerJobs(customerName, {
        page: 1,
        page_size: 50
      });
      setCustomerJobs(data.jobs || []);
      setSelectedCustomer(data.customer_name);
      setShowCustomerJobs(true);
    } catch (error) {
      console.error('Error loading customer jobs:', error);
    } finally {
      setOverlayLoading(false);
    }
  };

  return {
    customers,
    customerSearch,
    setCustomerSearch,
    customersPage,
    setCustomersPage,
    customersTotalPages,
    loadCustomers,
    customerJobs,
    loadCustomerJobs,
    customersOverlayLoading: overlayLoading
  };
};
